import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useListContext } from './ListContext';
import { Box } from '@mui/material';
// @ts-ignore
import InfiniteScroll from 'react-infinite-scroller';
import { SxProps } from '@mui/material';
import { useUiTheme } from 'framework/uiTheme';

type CollapsableListZoneProps<T> = {
  expanded?: boolean;
  items: T[];
  pageSize?: number;
  filterFn?: (item: T) => boolean;
  renderItem: (item: T, updateItem: (updated: T) => void) => React.ReactNode;
  renderHeader: (count: number) => React.ReactNode;
  customWrapperClassName?: string;
  sxWrapper?: SxProps;
  sxCollapseWrapper?: SxProps;
};

const CollapsableListZone = <T extends { id: string | number }>({
  items,
  pageSize = 20,
  filterFn,
  renderItem,
  renderHeader,
  expanded = false,
  customWrapperClassName = '',
  sxWrapper = {},
  sxCollapseWrapper,
}: CollapsableListZoneProps<T>) => {
  const { loading, updateInList } = useListContext();
  const [visibleItems, setVisibleItems] = useState<T[]>([]);
  const [collapse, setCollapse] = useState(expanded);
  const { Alert, Collapse, SmallLoader } = useUiTheme();

  const filteredItems = useMemo(() => {
    return filterFn ? items.filter(filterFn) : items;
  }, [items, filterFn]);

  const updateEntityInList = (updatedEntity: T) => {
    updateInList!(updatedEntity as any);
    setVisibleItems(prev => prev.map(item => (item.id === updatedEntity.id ? updatedEntity : item)));
  };

  const initFetchData = useCallback(() => {
    setVisibleItems(filteredItems.slice(0, pageSize));
  }, [filteredItems, pageSize]);

  const fetchMoreData = () => {
    const currentItemsCount = visibleItems.length;
    const nextItems = filteredItems.slice(currentItemsCount, currentItemsCount + pageSize);
    if (nextItems.length > 0) {
      setVisibleItems(prevItems => [...prevItems, ...nextItems]);
    }
  };

  useEffect(() => {
    if (loading) {
      setVisibleItems([]);
    } else {
      initFetchData();
    }
  }, [filteredItems, initFetchData, loading]);

  return (
    <Collapse
      unmountOnExit
      isExpanded={collapse}
      toggle={() => {
        setVisibleItems(prevItems => prevItems.slice(0, pageSize));
        setCollapse(prev => !prev);
      }}
      header={renderHeader(filteredItems.length)}
      customWrapperClassName={customWrapperClassName}
      sx={sxCollapseWrapper}
    >
      <Box sx={{ minHeight: '50px', position: 'relative', ...sxWrapper }}>
        {filteredItems.length ? (
          <InfiniteScroll
            style={{ overflow: 'auto' }}
            loadMore={fetchMoreData}
            hasMore={visibleItems.length < filteredItems.length}
            loader={<SmallLoader />}
          >
            <Box sx={{ minWidth: '1000px' }}>
              {visibleItems.map((item, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={i}>{renderItem(item, updateEntityInList)}</React.Fragment>
              ))}
            </Box>
          </InfiniteScroll>
        ) : (
          <Alert sx={{ alignItems: 'center' }} color="info">
            No records found
          </Alert>
        )}
      </Box>
    </Collapse>
  );
};

export default CollapsableListZone;
